import { Asset } from "expo-asset";
import * as ImagePicker from "expo-image-picker";
import { getVideoInfoAsync } from "expo-video-metadata";
import { ImageOff, X } from "lucide-react-native";
import { FC, useEffect, useState } from "react";
import { DimensionValue } from "react-native";

import { useLocalization } from "@/hooks";
import { isVideoExtension } from "@/types/utils";
import { Box, Center, Image, Pressable, Skeleton } from "./base";
import VideoPlayer from "./VideoPlayer";

interface MediaProps {
  alt?: string;
  className?: string;
  height?: DimensionValue;
  onDelete?: () => void;
  onPress?: () => void;
  resizeMode?: "cover" | "contain" | "stretch" | "center";
  showControls?: boolean;
  source: ImagePicker.ImagePickerAsset | string;
  width?: DimensionValue;
}

const getMediaNameFromUri = (uri: string): string => {
  const pathWithoutQuery = uri.split("?")[0];
  return pathWithoutQuery.split("/").pop() || "";
};

const getMediaTypeFromUri = (uri: string): "image" | "video" => {
  // Extract the path without query parameters for presigned URLs
  const pathWithoutQuery = uri.split("?")[0];
  const extension = pathWithoutQuery.split(".").pop()?.toLowerCase() || "";
  return isVideoExtension(extension) ? "video" : "image";
};

const Media: FC<MediaProps> = ({
  alt,
  className = "",
  height = 96,
  onDelete,
  onPress,
  resizeMode = "contain",
  showControls = true,
  source,
  width,
}) => {
  const { i18n } = useLocalization();
  const [assetData, setAssetData] = useState<Asset | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount] = useState(0);

  // Stable path for comparison - strips query params from pre-signed URLs
  const stablePath =
    typeof source === "string" ? source.split("?")[0] : source.uri;

  const loadImage = async (source: string) => {
    const asset = Asset.fromURI(source);
    await asset.downloadAsync();
    return asset;
  };

  const loadVideo = async (source: string) => {
    const result = await getVideoInfoAsync(source);
    return new Asset({
      height: result.height,
      name: getMediaNameFromUri(source),
      type: "video",
      uri: source,
      width: result.width,
    });
  };

  useEffect(() => {
    const loadAsset = async () => {
      setLoadError(false);
      setAssetData(null);

      // Handle ImagePickerAsset
      if (typeof source !== "string") {
        const type = source.type === "video" ? "video" : "image";
        setAssetData(
          new Asset({
            height: source.height || 0,
            name: source.fileName || "",
            type,
            uri: source.uri,
            width: source.width || 0,
          })
        );
        return;
      }

      // Handle string URI
      const mediaType = getMediaTypeFromUri(source);
      let asset: Asset;

      try {
        if (mediaType === "video") {
          asset = await loadVideo(source);
        } else {
          asset = await loadImage(source);
        }
        setAssetData(asset);
        setLoadError(false);
      } catch (error) {
        console.warn("Failed to load media metadata:", error);
        setLoadError(true);
        setAssetData(
          new Asset({
            height: 0,
            name: getMediaNameFromUri(source),
            type: mediaType,
            uri: source,
            width: 0,
          })
        );
      }
    };

    loadAsset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stablePath, retryCount]); // Reload when retry count changes

  if (!assetData) {
    return (
      <Box
        className="relative rounded-lg overflow-visible"
        style={{
          height: height,
          width: width ?? height,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Pressable
          className="w-full h-full"
          disabled={!onPress}
          onPress={onPress}
          // @ts-expect-error Web-specific styles
          style={{ cursor: onPress ? "pointer" : "default" }}
        >
          <Skeleton className="w-full h-full rounded-lg border border-outline-200" />
        </Pressable>
        {onDelete && (
          <Pressable
            className="absolute -top-2 -right-2 w-6 h-6 bg-error-500 rounded-full items-center justify-center shadow-md"
            onPress={onDelete}
            style={{
              opacity: isHovered ? 1 : 0,
              pointerEvents: isHovered ? "auto" : "none",
              transform: [{ scale: isHovered ? 1 : 0.8 }],
              // @ts-expect-error Web-specific styles
              transition:
                "opacity 150ms ease-in-out, transform 150ms ease-in-out",
            }}
          >
            <X color="rgb(var(--color-typography-0))" size={12} />
          </Pressable>
        )}
      </Box>
    );
  }

  const aspectRatio =
    assetData.width && assetData.height
      ? assetData.width / assetData.height
      : undefined;
  const isNumericWidth = typeof width === "number";
  const isNumericHeight = typeof height === "number";
  const numericHeight = isNumericHeight ? height : 96;
  const numericWidth = isNumericWidth ? width : undefined;
  const containerWidth =
    width ?? (aspectRatio ? numericHeight * aspectRatio : numericHeight);
  const containerHeight =
    height ?? (aspectRatio ? (numericWidth || 96) / aspectRatio : numericWidth);

  const handleImageError = () => {
    setLoadError(true);
  };

  const mediaContent = loadError ? (
    <Center
      className={`w-full h-full rounded-lg border border-outline-200 bg-background-50 ${className}`}
    >
      <ImageOff color="rgb(var(--color-typography-400))" size={24} />
    </Center>
  ) : (
    <Box
      className={`w-full h-full rounded-lg overflow-hidden border border-outline-200 ${className}`}
    >
      {assetData.type === "video" ? (
        <VideoPlayer
          aspectRatio={aspectRatio}
          className="w-full h-full"
          showControls={showControls}
          uri={assetData.uri}
        />
      ) : (
        <Image
          alt={alt || i18n.t("misc.selectedImageAlt", { index: 1 })}
          className="w-full h-full"
          onError={handleImageError}
          resizeMode={resizeMode}
          source={{ uri: assetData.uri }}
        />
      )}
    </Box>
  );

  return (
    <Box
      className="relative rounded-lg overflow-visible"
      style={{
        aspectRatio:
          !isNumericWidth || !isNumericHeight ? undefined : aspectRatio,
        height: containerHeight,
        width: containerWidth,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {onPress ? (
        <Pressable
          className="w-full h-full"
          onPress={onPress}
          style={{ cursor: "pointer" }}
        >
          {mediaContent}
        </Pressable>
      ) : (
        mediaContent
      )}
      {onDelete && (
        <Pressable
          className="absolute -top-2 -right-2 w-6 h-6 bg-error-500 rounded-full items-center justify-center shadow-md"
          onPress={onDelete}
          style={{
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? "auto" : "none",
            transform: [{ scale: isHovered ? 1 : 0.8 }],
            // @ts-expect-error Web-specific styles
            transition:
              "opacity 150ms ease-in-out, transform 150ms ease-in-out",
          }}
        >
          <X color="rgb(var(--color-typography-0))" size={12} />
        </Pressable>
      )}
    </Box>
  );
};

export default Media;
